// Where the access token lives: in this component's memory, and nowhere else.
//
// Not localStorage, not sessionStorage, not a cookie this code can read. A token
// in any of those is a token that a cross-site scripting flaw anywhere on this
// origin can exfiltrate, and it survives the tab that earned it. Held here, it
// dies with the page — which is exactly why the refresh cookie exists, and why
// the boot sequence below is one silent renewal rather than a stored session.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import {
  PortalRequestFailed,
  requestIdentity,
  requestRefresh,
  requestScopeSwitch,
  requestSignIn,
  requestSignOut,
  type ActingScope,
} from './api';
import { PortalSessionContext, type PortalSession, type PortalSessionValue } from './context';

export function PortalSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<PortalSession>({ status: 'checking' });

  // A ref rather than state: a request needs the token that is current at the
  // moment it is sent, and a renewal that has not re-rendered yet would
  // otherwise hand the stale one to the retry it just triggered.
  const accessToken = useRef<string | null>(null);

  const establish = useCallback(async (token: string) => {
    const identity = await requestIdentity(token);
    accessToken.current = token;
    setSession({ status: 'signed-in', identity });
  }, []);

  const abandon = useCallback(() => {
    accessToken.current = null;
    setSession({ status: 'signed-out' });
  }, []);

  // One silent renewal on boot, and it is the whole of how the portal decides
  // whether somebody is signed in. There is no stored flag to consult: either
  // the browser still holds a refresh cookie the backend will honour, or it
  // does not.
  useEffect(() => {
    let current = true;

    (async () => {
      try {
        const granted = await requestRefresh();
        if (current) {
          await establish(granted.accessToken);
        }
      } catch (failure) {
        if (!(failure instanceof PortalRequestFailed)) {
          throw failure;
        }
        // No usable cookie is not an error here — it is what signed-out
        // looks like on a first visit or after a sign-out.
        if (current) {
          abandon();
        }
      }
    })();

    return () => {
      current = false;
    };
  }, [establish, abandon]);

  const signIn = useCallback(
    async (email: string, password: string, keepSignedIn: boolean) => {
      const granted = await requestSignIn(email, password, keepSignedIn);
      await establish(granted.accessToken);
    },
    [establish],
  );

  // The claim journey's door into this session. `establish` re-reads the
  // identity from the backend rather than trusting what the claim reported, so
  // whichever door somebody came through, what this tab believes about who they
  // are came from GET /portal/api/me/ and from nowhere else.
  const adoptSession = useCallback(
    async (token: string) => {
      await establish(token);
    },
    [establish],
  );

  // The switcher's door into this session, and it ends where every other door
  // ends: `establish` re-reads GET /portal/api/me/. So the shape this tab
  // draws after a switch came from the backend's answer and not from what the
  // switch asked for — which matters more here than at the sign-in door,
  // because a switch is precisely the moment the surface changes. A refusal
  // leaves the current session untouched: nothing is dropped before the new
  // grant is in hand.
  const switchScope = useCallback(
    async (scope: ActingScope) => {
      const granted = await requestScopeSwitch(scope);
      await establish(granted.accessToken);
    },
    [establish],
  );

  const signOut = useCallback(async () => {
    // The token is dropped whatever the request does. A sign-out that left the
    // tab holding a live token because the network hiccuped would be a
    // sign-out that did not sign anybody out of the screen in front of them.
    try {
      await requestSignOut();
    } finally {
      abandon();
    }
  }, [abandon]);

  const authorizedFetch = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const send = (token: string) =>
        fetch(path, {
          ...init,
          credentials: 'same-origin',
          headers: { ...init.headers, Authorization: `Bearer ${token}` },
        });

      if (!accessToken.current) {
        throw new PortalRequestFailed(401, 'There is no signed-in session');
      }

      const first = await send(accessToken.current);
      if (first.status !== 401) {
        return first;
      }

      // The access token aged out mid-session. One renewal, then one retry —
      // never a loop, so a service that answers 401 to everything cannot turn
      // a page into a refresh storm.
      try {
        const granted = await requestRefresh();
        accessToken.current = granted.accessToken;
      } catch (failure) {
        if (!(failure instanceof PortalRequestFailed)) {
          throw failure;
        }
        abandon();
        return first;
      }

      return send(accessToken.current);
    },
    [abandon],
  );

  const value = useMemo<PortalSessionValue>(
    () => ({ session, signIn, adoptSession, switchScope, signOut, authorizedFetch }),
    [session, signIn, adoptSession, switchScope, signOut, authorizedFetch],
  );

  return <PortalSessionContext.Provider value={value}>{children}</PortalSessionContext.Provider>;
}
